import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Plus, Layout, Calendar, Wrench,
  Sparkles, CheckSquare, Activity,
  MousePointer2, Info, LucideIcon,
  Shield, Users, Lock, Receipt, PawPrint, Car, Trophy,
  DollarSign, UtensilsCrossed, Heart, MapPin
} from 'lucide-react';
// 🛰️ Using unified types to ensure widget IDs match the Registry
import { WidgetId } from '@/types';

interface WidgetSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onAddWidget: (id: WidgetId, defaultW: number, defaultH: number) => void;
  activeWidgetIds: string[];
  // 🛡️ Hybrid Scope Props
  currentHouseholdId: string | null;
  conditionalFlags?: {
    hasCoParenting: boolean;
    hasElderCare: boolean;
    hasMunicipal: boolean;
  };
}

interface WidgetDefinition {
  id: WidgetId;
  label: string;
  description: string;
  icon: LucideIcon; 
  defaultSize: { w: number; h: number };
  category: 'productivity' | 'household' | 'intelligence';
  scopeRequired: 'none' | 'household'; // 🛡️ NEW: Logic to gate shared widgets
  conditionalFlag?: 'hasCoParenting' | 'hasElderCare' | 'hasMunicipal';
}

/**
 * 🗄️ WidgetSidebar
 * Refactored for Hybrid Scoping: Catalogues personal and shared-ready modules.
 * Strictly silos personal layout preferences in /users/{uid}.
 */
export const WidgetSidebar: React.FC<WidgetSidebarProps> = ({
  isOpen,
  onClose,
  onAddWidget,
  activeWidgetIds,
  currentHouseholdId,
  conditionalFlags
}) => {
  const { t } = useTranslation();

  const AVAILABLE_WIDGETS: WidgetDefinition[] = [
    { 
      id: 'quick_access', 
      label: t('dash.widget.quick_access.title', 'Quick Actions'), 
      description: t('dash.widget.quick_access.desc', 'One-tap creation for tasks and events.'),
      icon: Plus, 
      defaultSize: { w: 10, h: 4 },
      category: 'productivity',
      scopeRequired: 'none'
    },
    { 
      id: 'calendar_agenda', 
      label: t('dash.widget.calendar.title', 'Agenda'), 
      description: t('dash.widget.calendar.desc', 'Your upcoming schedule in a vertical feed.'),
      icon: Calendar, 
      defaultSize: { w: 10, h: 12 },
      category: 'productivity',
      scopeRequired: 'none'
    },
    { 
      id: 'tasks_summary', 
      label: t('dash.widget.tasks.title', 'Task Pulse'), 
      description: t('dash.widget.tasks.desc', 'Filterable list of your active workstreams.'),
      icon: CheckSquare, 
      defaultSize: { w: 10, h: 12 },
      category: 'productivity',
      scopeRequired: 'none'
    },
    { 
      id: 'maintenance_agenda', 
      label: t('dash.widget.maint.title', 'Home Systems'), 
      description: t('dash.widget.maint.desc', 'Predicted maintenance for household assets.'),
      icon: Wrench, 
      defaultSize: { w: 10, h: 8 },
      category: 'household',
      scopeRequired: 'household'
    },
    { 
      id: 'activity_log', 
      label: t('dash.widget.activity.title', 'Household Feed'), 
      description: t('dash.widget.activity.desc', 'Real-time log of household coordination.'),
      icon: Activity, 
      defaultSize: { w: 10, h: 16 },
      category: 'household',
      scopeRequired: 'household'
    },
    {
      id: 'sparks',
      label: t('dash.widget.sparks.title', 'Intelligence Feed'),
      description: t('dash.widget.sparks.desc', 'Curated insights from the Knowledge Engine.'),
      icon: Sparkles,
      defaultSize: { w: 10, h: 8 },
      category: 'intelligence',
      scopeRequired: 'none'
    },
    {
      id: 'bills_summary' as WidgetId,
      label: 'Bills Overview',
      description: 'Monthly spend total and upcoming due dates.',
      icon: Receipt,
      defaultSize: { w: 10, h: 6 },
      category: 'household',
      scopeRequired: 'household',
    },
    {
      id: 'pets_overview' as WidgetId,
      label: 'Pet Care',
      description: "Today's pet care tasks at a glance.",
      icon: PawPrint,
      defaultSize: { w: 10, h: 8 },
      category: 'household',
      scopeRequired: 'household',
    },
    {
      id: 'vehicles_summary' as WidgetId,
      label: 'Vehicles',
      description: 'Your registered vehicles and service status.',
      icon: Car,
      defaultSize: { w: 10, h: 6 },
      category: 'household',
      scopeRequired: 'household',
    },
    {
      id: 'leaderboard' as WidgetId,
      label: 'Leaderboard',
      description: 'Monthly points rankings for all household members.',
      icon: Trophy,
      defaultSize: { w: 8, h: 6 },
      category: 'household',
      scopeRequired: 'household',
    },
    {
      id: 'daily_brief' as WidgetId,
      label: 'Daily Brief',
      description: 'AI-generated summary of your day — tasks, events, and weather.',
      icon: Sparkles,
      defaultSize: { w: 12, h: 7 },
      category: 'intelligence',
      scopeRequired: 'none',
    },
    {
      id: 'health_score' as WidgetId,
      label: 'Household Health',
      description: 'AI-computed health score across tasks, grocery, maintenance, and bills.',
      icon: Sparkles,
      defaultSize: { w: 6, h: 5 },
      category: 'intelligence',
      scopeRequired: 'household',
    },
    {
      id: 'finance_summary' as WidgetId,
      label: 'Finance Overview',
      description: 'Monthly spend totals and top spending categories.',
      icon: DollarSign,
      defaultSize: { w: 10, h: 7 },
      category: 'household',
      scopeRequired: 'household',
    },
    {
      id: 'meal_planner_week' as WidgetId,
      label: 'Meal Planner',
      description: "This week's meal plan at a glance.",
      icon: UtensilsCrossed,
      defaultSize: { w: 12, h: 6 },
      category: 'household',
      scopeRequired: 'none',
    },
    {
      id: 'co_parenting_summary' as WidgetId,
      label: 'Co-Parenting',
      description: 'Upcoming custody handoffs and child appointments.',
      icon: Users,
      defaultSize: { w: 10, h: 8 },
      category: 'household',
      scopeRequired: 'household',
      conditionalFlag: 'hasCoParenting',
    },
    {
      id: 'appliance_overview' as WidgetId,
      label: 'Appliances',
      description: 'Top appliances by risk score with recall alerts.',
      icon: Wrench,
      defaultSize: { w: 10, h: 7 },
      category: 'household',
      scopeRequired: 'household',
    },
    {
      id: 'elder_care_summary' as WidgetId,
      label: 'Elder Care',
      description: 'Medications due and upcoming care appointments.',
      icon: Heart,
      defaultSize: { w: 10, h: 8 },
      category: 'household',
      scopeRequired: 'household',
      conditionalFlag: 'hasElderCare',
    },
    {
      id: 'location_overview' as WidgetId,
      label: 'Family Location',
      description: 'See which household members are sharing their location.',
      icon: MapPin,
      defaultSize: { w: 10, h: 7 },
      category: 'household',
      scopeRequired: 'household',
    },
  ];

  return (
    <aside 
      className={`fixed top-20 right-0 bottom-0 w-84 bg-white dark:bg-slate-900 border-l border-slate-100 dark:border-slate-800 shadow-2xl z-[90] transition-transform duration-500 ease-out transform ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {/* --- HEADER --- */}
      <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Layout className="w-5 h-5 text-brand" />
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white">
            {t('dash.widget_library', 'Widget Library')}
          </h2>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* --- PRIVACY PROTOCOL BANNER --- */}
      <div className="p-4 bg-slate-50 dark:bg-slate-950/50 space-y-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
          <Shield className="w-3.5 h-3.5 text-emerald-500" />
          <p className="text-[9px] font-black text-slate-500 uppercase tracking-tighter leading-none">
            {t('dash.layout_privacy', 'Layouts are strictly personal & private')}
          </p>
        </div>
        {!currentHouseholdId && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800">
            <Lock className="w-3.5 h-3.5 text-amber-500" />
            <p className="text-[9px] font-black text-amber-600 uppercase tracking-tighter leading-none">
              {t('dash.shared_locked', 'Some modules require a Household link')}
            </p>
          </div>
        )}
      </div>

      {/* --- WIDGET LIST --- */}
      <div className="overflow-y-auto h-[calc(100%-200px)] p-4 space-y-4 no-scrollbar">
        {AVAILABLE_WIDGETS.map((widget: WidgetDefinition) => {
          const isAdded = activeWidgetIds.includes(widget.id);
          const isLocked = widget.scopeRequired === 'household' && !currentHouseholdId;
          const isConditionalHidden = !!(widget.conditionalFlag && conditionalFlags && !conditionalFlags[widget.conditionalFlag]);
          if (isConditionalHidden) return null;
          const Icon = widget.icon;

          return (
            <button
              key={widget.id}
              disabled={isAdded || isLocked}
              onClick={() => onAddWidget(widget.id, widget.defaultSize.w, widget.defaultSize.h)}
              className={`w-full text-left p-5 rounded-[2rem] border-2 transition-all group relative overflow-hidden ${
                isAdded || isLocked
                  ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700 opacity-60 cursor-not-allowed'
                  : 'bg-white dark:bg-slate-900 border-slate-50 dark:border-slate-800 hover:border-brand hover:shadow-xl active:scale-[0.96]'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-2xl transition-colors ${
                  isAdded || isLocked ? 'bg-slate-200 text-slate-400' : 'bg-indigo-50 dark:bg-indigo-900/30 text-brand group-hover:bg-brand group-hover:text-white shadow-sm'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[11px] font-black uppercase tracking-tight text-slate-900 dark:text-white leading-none">
                      {widget.label}
                    </h3>
                    {widget.scopeRequired === 'household' ? (
                      <Users className="w-2.5 h-2.5 text-brand/50" />
                    ) : (
                      <Shield className="w-2.5 h-2.5 text-emerald-500/50" />
                    )}
                  </div>
                  <p className="text-2xs font-bold text-slate-500 dark:text-slate-400 mt-1.5 leading-snug">
                    {widget.description}
                  </p>
                  
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <Layout className="w-2.5 h-2.5 text-slate-400" />
                      <span className="text-[8px] font-black text-slate-500 uppercase">
                        {widget.defaultSize.w}x{widget.defaultSize.h}
                      </span>
                    </div>
                    {isAdded && (
                      <span className="text-[8px] font-black text-brand uppercase tracking-widest flex items-center gap-1">
                        <MousePointer2 className="w-2.5 h-2.5" /> {t('dash.added', 'Active')}
                      </span>
                    )}
                    {isLocked && (
                      <span className="text-[8px] font-black text-amber-500 uppercase tracking-widest flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> {t('dash.locked', 'Household Needed')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className={`absolute top-0 right-0 w-1.5 h-full ${
                widget.category === 'productivity' ? 'bg-blue-500' : 
                widget.category === 'household' ? 'bg-emerald-500' : 'bg-amber-500'
              }`} />
            </button>
          );
        })}
      </div>
    </aside>
  );
};